import LookupService from "./lookupService";
import {appCache} from "../../cache/slices/app/appSlice";
import api from "./api";
import {DateTime} from "luxon";
import {
    booleanToString,
    dateToStringWithFormat,
    DEFAULT_TIME_FORMAT,
    DEFAULT_DATE_TIME_FORMAT,
    defaultTimeAsString,
    formatSignatureForWrite,
    stringToDateWithFormat,
    APPOINTMENT_START_STOP_DATE_TIME_FORMAT
} from "../utils/formatUtils";
import syncService from "./syncService";
import activityLogRepository from "../../db/activityLogRepository";
import {evvRepository} from "../../db/evv";
import {getDispatchFromConfig} from "../utils/miscUtils";
import { canProcess, shouldRetry, SYNC_STATUS } from "../utils/syncUtils";
import { SYNC_STATE_DIRTY, SYNC_STATE_CLEAN, SYNC_STATE_ERROR } from "../constants";
import { nowAsDateTime } from "../utils/dateTimeUtils";
import { emitDeadlockIfDetected, ERROR_SOURCES } from "../utils/syncError";
import ApiRetryHandler from "../utils/handler/ApiRetryHandler";

const VISIT_PENDING = -1;
const VISIT_STARTED = 0;
const VISIT_COMPLETE = 1;

const EVENT_TYPES = {
    NULL_POSITION: 'VISIT_NULL_POSITION',
    EXCEPTION: 'VISIT_EXCEPTION',
    FIELD_NULL: 'VISIT_FIELD_NULL_DETECTED',
    FIELD_SNAPSHOT: 'VISIT_FIELD_SNAPSHOT'
};

const ACTION_TYPES = {
    START: 'START_SESSION',
    STOP: 'STOP_SESSION',
    CANCEL_VERIFY: 'CANCEL_VERIFY',
    VERIFY: 'VERIFY_SESSION',
    AFTER_DB_WRITE: 'AFTER_DB_WRITE',
    UPDATE_FROM_APPOINTMENT: 'UPDATE_FROM_APPOINTMENT'
};

const SOURCES = {
    VISIT_START_INVALID_POS: 'startClientVisit.invalidPosition',
    VISIT_START_EXCEPTION: 'startClientVisit.exception',
    VISIT_START_SAVE_ERROR: 'startClientVisit.saveVisitError',
    VISIT_START_LOAD_ERROR: 'startClientVisit.loadVisitError',
    VISIT_START_SNAPSHOT: 'startClientVisit.beforeSave',
    VISIT_END_INVALID_POS: 'endClientVisit.invalidPosition',
    VISIT_END_POS_GUARD_ERROR: 'endClientVisit.positionGuardError',
    VISIT_END_SNAPSHOT: 'endClientVisit.beforeSave',
    VISIT_END_LOAD_ERROR: 'endClientVisit.loadActiveVisitError',
    CANCEL_VERIFY_SNAPSHOT: 'cancelVerifyClientVisit.beforeCleanup',
    VERIFY_SNAPSHOT_MERGE: 'verifyClientVisit.afterMerge',
    VERIFY_SNAPSHOT_SAVE: 'verifyClientVisit.beforeSaveVisit',
    SAVE_VISIT_AFTER_UPDATE: 'saveVisit.afterEvvVisitIdUpdate',
    SAVE_VISIT_FOR_APPOINTMENT: 'saveVisitForAppointment.afterMerge',
    SAVE_VISIT_ENTRY: 'saveVisit.entrySnapshot',
    SAVE_VISIT_START_BEGIN_DATETIME: 'saveVisit.startBeginDateTime',
    SAVE_VISIT_VERIFY_END_DATETIME: 'saveVisit.verifyEndDateTime'
};

class VisitService extends LookupService{
    loadFromRepository = (config) => {
        return this.repository.loadAllByProperty('complete', VISIT_STARTED);
    }

    buildTrackedVisitFields = (visit) => {
        return {
            beginActualDateTime: visit?.beginActualDateTime,
            startLatitude: visit?.startLatitude,
            startLongitude: visit?.startLongitude,
            endActualDateTime: visit?.endActualDateTime,
            endLatitude: visit?.endLatitude,
            endLongitude: visit?.endLongitude
        };
    }

    getNullTrackedFields = (visit) => {
        const trackedFields = this.buildTrackedVisitFields(visit);
        return Object.keys(trackedFields).filter((key) => trackedFields[key] == null);
    }

    trackVisitDeviceInfo = ({ eventType, actionType, source, user, visit }) => {
        const nullTrackedFields = this.getNullTrackedFields(visit);
        syncService.deviceInfoService.trackDeviceInfo({
            eventType,
            actionType,
            source,
            user,
            visit
        });

        if (nullTrackedFields.length > 0) {
            syncService.deviceInfoService.trackDeviceInfo({
                eventType: EVENT_TYPES.FIELD_NULL,
                actionType,
                source,
                user,
                visit
            });
        }
    }

    saveVisit = (visit, config) => {
        syncService.deviceInfoService.trackDeviceInfo({
            eventType: EVENT_TYPES.FIELD_SNAPSHOT,
            actionType: ACTION_TYPES.AFTER_DB_WRITE,
            source: SOURCES.SAVE_VISIT_ENTRY,
            user: config?.user,
            visit: visit
        });

        if (visit) {
            if (visit.visitStatus === 'start' && !visit.beginActualDateTime) {
                visit.beginActualDateTime = dateToStringWithFormat(DateTime.now(), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);

                syncService.deviceInfoService.trackDeviceInfo({
                    eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                    actionType: ACTION_TYPES.AFTER_DB_WRITE,
                    source: SOURCES.SAVE_VISIT_START_BEGIN_DATETIME,
                    user: config?.user,
                    visit: visit
                });

            } else if (visit.visitStatus === 'verify' && !visit.endActualDateTime) {
                visit.endActualDateTime = dateToStringWithFormat(DateTime.now(), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);

                syncService.deviceInfoService.trackDeviceInfo({
                    eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                    actionType: ACTION_TYPES.AFTER_DB_WRITE,
                    source: SOURCES.SAVE_VISIT_VERIFY_END_DATETIME,
                    user: config?.user,
                    visit: visit
                });
            }

            const isOffline = this.isInformationOffline(config);
            visit.isRecordOnline = !isOffline;
            const visits = [visit];

            if (visit.visitStatus !== 'end') {
                visit.syncState = SYNC_STATE_DIRTY;
            } else {
                visit.syncState = visit.syncState || SYNC_STATE_CLEAN;
            }

            return this.save(visits, config)
                .then(async () => {
                    if (isOffline) {
                        const dispatch = getDispatchFromConfig(config);
                        const offlineVisit = visits[0];

                        if (visit.visitStatus === 'start') {    
                            offlineVisit.isPendSendStartSessInfo = true;
                            visits[0] = offlineVisit;
                            
                            await this.save([offlineVisit], { store: config.store, doNotClear: true });
                            
                            dispatch(this.operationReplace(offlineVisit));
                        }

                        return offlineVisit;
                    }

                    if (visit.appointmentId && visit.appointmentId < 0 && visit.visitStatus === 'start') {
                        const linkedAppointment = await evvRepository.evvDb.appointment.get(visit.appointmentId)
                            ?? (visit.syncId ? await evvRepository.evvDb.appointment.filter(a => a.syncId === visit.syncId).first() : null);
                        if (linkedAppointment && shouldRetry(linkedAppointment.statusSync)) {
                            const dispatch = getDispatchFromConfig(config);
                            const errorVisit = {
                                ...visit,
                                statusSync: SYNC_STATUS.ERROR,
                                syncState: SYNC_STATE_DIRTY,
                                isPendSendStartSessInfo: true
                            };
                            await this.save([errorVisit], { store: config.store, doNotClear: true });
                            
                            if (dispatch) {
                                dispatch(this.operationReplace(errorVisit));
                            }

                            return errorVisit;
                        }
                    }

                    if (visit.evvVisitId < 0 && visit.appointmentId < 0) {
                        const linkedAppointment = await evvRepository.evvDb.appointment.get(visit.appointmentId)
                            ?? (visit.syncId ? await evvRepository.evvDb.appointment.filter(a => a.syncId === visit.syncId).first() : null);
                        
                        if (linkedAppointment && linkedAppointment.evvVisitId > 0 && linkedAppointment.appointmentId > 0) {
                            const currentVisitInDb = await evvRepository.evvDb.visit.get(visit.evvVisitId);
                            const oldEvvVisitId = visit.evvVisitId;
                            visit.evvVisitId = linkedAppointment.evvVisitId;
                            visit.appointmentId = linkedAppointment.appointmentId;
                            visit.appointment = linkedAppointment;

                            if (currentVisitInDb?.isPendSendStartSessInfo) {
                                visit.isPendSendStartSessInfo = true;
                            }
                            
                            visits[0] = visit;
                            await evvRepository.evvDb.visit.put(visit);
                            await evvRepository.evvDb.visit.delete(oldEvvVisitId);

                            syncService.deviceInfoService.trackDeviceInfo({
                                eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                                actionType: ACTION_TYPES.AFTER_DB_WRITE,
                                source: SOURCES.SAVE_VISIT_AFTER_UPDATE,
                                user: config?.user,
                                visit: visit
                            });
                        }
                    }

                    if (visit.visitStatus === 'end') {
                        return visits[0];
                    }

                    if (visit.statusSync && !canProcess(visit.statusSync)) {
                        if (visit.visitStatus === 'start' && !visit.isPendSendStartSessInfo) {
                            visit.isPendSendStartSessInfo = true;
                            visits[0] = visit;
                            await this.save([visit], { store: config.store, doNotClear: true });
                            
                            const dispatch = getDispatchFromConfig(config);
                            if (dispatch) {
                                dispatch(this.operationReplace(visit));
                            }
                        }

                        return visits[0];
                    }

                    console.log('visitService: saveVisit - About to call writeVisitsToServer');
                    return this.writeVisitsToServer(visits, config)
                        .then(savedVisits => {
                            console.log('visitService: saveVisit - writeVisitsToServer completed, processing savedVisits');
                            let savedVisit = null;

                            if (savedVisits && savedVisits.length > 0) {
                                const dispatch = getDispatchFromConfig(config);
                                savedVisit = savedVisits[0];

                                if (visit.visitStatus === 'start') {
                                    dispatch(this.operationReplace(savedVisit));
                                } else if (visit.visitStatus !== 'pending') {
                                    if (visit.complete === VISIT_COMPLETE) {
                                        syncService.visitService.load(config);
                                        dispatch(this.operationRemove(savedVisit));
                                    } else {
                                        dispatch(this.operationReplace(savedVisit));
                                    }
                                }
                            }

                            console.log('visitService: saveVisit - Returning savedVisit from writeVisitsToServer.then()');
                            return savedVisit;
                        })
                })
        }
    }

    createInitialVisit = (appointment) => {
        // Use scheduledStartDateInstance if available (preserved scheduled times)
        // Otherwise fall back to startDateInstance (which might have been modified during Start Session)
        const scheduledStart = appointment.scheduledStartDateInstance || appointment.startDateInstance;
        const scheduledEnd = appointment.scheduledEndDateInstance || appointment.endDateInstance;
        
        return {
            evvVisitId: appointment.evvVisitId ? appointment.evvVisitId : -1,
            deviceVisitId: Date.now(),
            appointmentId: appointment.appointmentId,
            appointment: appointment,
            client: appointment.client,
            clientId: appointment.client.clientId,
            staffId: appointment.staffId,
            visitStatus: 'pending',
            complete: VISIT_PENDING,
            syncId: appointment.syncId,
            statusSync: appointment.statusSync,
            beginDateTime: scheduledStart?.toMillis(),
            endDateTime: scheduledEnd?.toMillis()
        };
    }

    updateVisitFromAppointment = (visit , appointment) => {
        const visitToUpdate = visit ? visit : this.createInitialVisit(appointment);

        // Use scheduled times if available, otherwise use current appointment times
        const scheduledStart = appointment.scheduledStartDateInstance || appointment.startDateInstance;
        const scheduledEnd = appointment.scheduledEndDateInstance || appointment.endDateInstance;

        return {
            ...visitToUpdate,
            actualStartDateInstance: appointment.startDateInstance,
            actualEndDateInstance: appointment.endDateInstance,
            beginActualDateTime: visit?.originalStartDate ? dateToStringWithFormat(DateTime.fromMillis(visit.originalStartDate), APPOINTMENT_START_STOP_DATE_TIME_FORMAT) : dateToStringWithFormat(appointment.startDateInstance, APPOINTMENT_START_STOP_DATE_TIME_FORMAT),
            endActualDateTime: visit?.originalEndDate ? dateToStringWithFormat(DateTime.fromMillis(visit.originalEndDate), APPOINTMENT_START_STOP_DATE_TIME_FORMAT) : dateToStringWithFormat(appointment.endDateInstance, APPOINTMENT_START_STOP_DATE_TIME_FORMAT),
            startDateTime: appointment.startDateInstance.toMillis(),
            beginDateTime: visitToUpdate.beginDateTime || scheduledStart.toMillis(),
            endDateTime: visit?.visitStatus === 'pending' ? scheduledEnd.toMillis() : (visitToUpdate.endDateTime ? visitToUpdate.endDateTime : scheduledEnd.toMillis()),
            syncId: appointment.syncId || visitToUpdate.syncId,
            statusSync: appointment.statusSync || visitToUpdate.statusSync,
        };
    }

    saveVisitForAppointment = (appointment, config) => {
        return this.loadVisitByAppointmentId(appointment.appointmentId, config)
            .then(visit => {
                console.log('visit loaded for appointment');
                console.log(visit);
                const visitToSave = this.updateVisitFromAppointment(visit, appointment);

                // If a session is already active, updateVisitFromAppointment overwrites
                // beginActualDateTime/endActualDateTime with the scheduled appointment times.
                // Restore the actual recorded times so they aren't lost.
                if (visit && (visit.visitStatus === 'start' || visit.visitStatus === 'end')) {
                    if (visit.beginActualDateTime) visitToSave.beginActualDateTime = visit.beginActualDateTime;
                    if (visit.endActualDateTime)   visitToSave.endActualDateTime   = visit.endActualDateTime;
                    visitToSave.startLatitude  = visit.startLatitude;
                    visitToSave.startLongitude = visit.startLongitude;
                    visitToSave.endLatitude    = visit.endLatitude;
                    visitToSave.endLongitude   = visit.endLongitude;
                    visitToSave.isPendSendStartSessInfo = visit.isPendSendStartSessInfo;
                }

                if (this.isInformationOffline(config)) {
                    visitToSave.beginEndDateTimeOffline = true;
                }
                
                this.trackVisitDeviceInfo({
                    eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                    actionType: ACTION_TYPES.UPDATE_FROM_APPOINTMENT,
                    source: SOURCES.SAVE_VISIT_FOR_APPOINTMENT,
                    user: config?.user,
                    visit: visitToSave
                });
                
                console.log('updated visit from appointment');
                console.log(visitToSave);
                return this.saveVisit(visitToSave, config);
            });
    }

    beforeSave = (visits, config) => {
        return visits;
    }

    afterLoad = (visits, config) => {
        if (visits && visits[0] !== undefined && visits.length > 0){
            const state = config.store.getState();
            const clients = appCache.getClients(state);
            const appointments = syncService.getAppointments(state);

            for (const visit of visits) {
                const appointment = appointments && appointments.find(a => a.appointmentId === visit.appointmentId);

                if (appointment) {
                    visit.appointment = appointment;
                }

                // eslint-disable-next-line eqeqeq
                const client = clients && clients.find(c => c.clientId == visit.clientId);
                visit.client = client ? client : appointment.client;

                visit.actualStartDateInstance = stringToDateWithFormat(visit.beginActualDateTime, APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
                visit.actualEndDateInstance = stringToDateWithFormat(visit.endActualDateTime, APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
            }
        }

        return visits;
    }

    afterLoadAsync = async (visits, config) => {
        return this.afterLoad(visits, config);
    }

    createWriteVisit = (visit) => {
        const appointment = visit.appointment;
        let evvVisitId = visit.evvVisitId;

        if (appointment.evvVisitId && appointment.evvVisitId >= 0 && appointment.evvVisitId !== visit.evvVisitId){
            evvVisitId = appointment.evvVisitId;
        }

        if (evvVisitId === "" || evvVisitId < 0){
            return null;
        }

        const writeVisit = {
            evvVisitId: evvVisitId,
            activityLogId: appointment.appointmentId,
        };
        
        if (writeVisit.activityLogId <= 0) {
            writeVisit.activityLogId = visit.appointmentId;
        }

        if (visit.visitStatus === 'pending') {
            writeVisit.beginDateTime = dateToStringWithFormat(DateTime.fromMillis(visit.startDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
            writeVisit.endDateTime = dateToStringWithFormat(DateTime.fromMillis(visit.endDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
        } else if (visit.visitStatus === 'start') {
            writeVisit.organizationId = appointment.organizationId;
            writeVisit.beginActualDateTime = visit.beginActualDateTime;
            writeVisit.startLatitude = visit.startLatitude;
            writeVisit.startLongitude = visit.startLongitude;
            if (typeof visit.isPendSendStartSessInfo === "boolean" && visit.isPendSendStartSessInfo) {
                writeVisit.beginDateTime = dateToStringWithFormat(DateTime.fromMillis(visit.beginDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
                writeVisit.endDateTime = dateToStringWithFormat(DateTime.fromMillis(visit.endOriginalDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
            }
        } else {
            writeVisit.organizationId = appointment.organizationId;
            writeVisit.endLatitude = visit.endLatitude;
            writeVisit.endLongitude = visit.endLongitude;
            if (typeof visit.isPendSendStartSessInfo === "boolean" && visit.isPendSendStartSessInfo) {
                writeVisit.endActualDateTime = visit.endActualDateTime;
                writeVisit.beginActualDateTime = visit.beginActualDateTime;
                writeVisit.startLatitude = visit.startLatitude;
                writeVisit.startLongitude = visit.startLongitude;
                writeVisit.beginDateTime = dateToStringWithFormat(DateTime.fromMillis(visit.beginDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
                writeVisit.endDateTime = dateToStringWithFormat(DateTime.fromMillis(visit.endOriginalDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
            }
        }

        if (visit.visitStatus === 'verify') {
            writeVisit.endActualDateTime = visit.endActualDateTime;
            if (visit.isUpdatedBeginAdjustedDateTime)
                writeVisit.beginAdjustedDateTime = visit.beginAdjustedDateTime;
            if (visit.isUpdatedEndAdjustedDateTime)
                writeVisit.endAdjustedDateTime = visit.endAdjustedDateTime;
            writeVisit.servicesVerified = booleanToString(visit.servicesVerified);
            writeVisit.timesVerified = booleanToString(visit.timesVerified);
            writeVisit.clientSig = formatSignatureForWrite(visit.signature);
            writeVisit.hasSig = booleanToString(visit.hasSignature);
            if (typeof visit.isPendSendStartSessInfo === "boolean" && visit.isPendSendStartSessInfo) {
                writeVisit.beginActualDateTime = visit.beginActualDateTime;
                writeVisit.startLatitude = visit.startLatitude;
                writeVisit.startLongitude = visit.startLongitude;
                writeVisit.beginDateTime = dateToStringWithFormat(DateTime.fromMillis(visit.beginDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
                writeVisit.endDateTime = dateToStringWithFormat(DateTime.fromMillis(visit.endOriginalDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
            }
            
            if (visit.editTimesReason){
                writeVisit.editTimeEventType = "CHANGETIME";
                writeVisit.editTimeEventAttr = visit.editTimesReason;
                writeVisit.editTimeEventValue = visit.editTimesComments;
            }

            if (visit.signatureMissingReason){
                writeVisit.clientSigMissingEventType = "CHANGESIGN";
                writeVisit.clientSigMissingEventAttr = visit.signatureMissingReason;
                writeVisit.clientSigMissingEventValue = visit.signatureMissingComments;
            }

            if (visit.verificationDeclinedReason){
                writeVisit.clientVerificationDeclinedEventType = "CHANGEVERIFICATION";
                writeVisit.clientVerificationDeclinedEventAttr = visit.verificationDeclinedReason;
                writeVisit.clientVerificationDeclinedEventValue = visit.verificationDeclinedComments;
            }

            if (visit.addressDistanceReason){
                writeVisit.addressDistanceEventType = "CHANGEDISTANCE";
                writeVisit.addressDistanceEventAttr = visit.addressDistanceReason;
                writeVisit.addressDistanceEventValue = visit.addressDistanceComments;
            }
        }

        return writeVisit;
    }

    writeVisitsToServer = async (nakedVisits, config) => {
        const visits = await this.afterLoadAsync(nakedVisits, config);
        const writeVisits = visits.map(visit => this.createWriteVisit(visit)).filter(visit => visit != null);

        if (writeVisits.length === 0){
            return Promise.resolve(visits);
        }

        const writeVisitsRequest = {
            resources: writeVisits
        };

        console.log("Writing visits to server: ");
        console.log(writeVisitsRequest);

        // Retry mechanism for intermittent connectivity
        const retryHandler = new ApiRetryHandler({
            maxRetries: 3,
            retryDelay: 1000,
            useExponentialBackoff: false,
            context: 'writeVisit'
        });

        return retryHandler.execute(
            () => api.post(this.writeUrl, this.getFetchConfig(config, writeVisitsRequest))
        )
            .then(async writeVisitsResponse => {
                const visitsToSave = [];
                let hasDeadlock = false;
                console.log("Succes writing visits to server: ");
                console.log(writeVisitsResponse);
                if (writeVisitsResponse.errors && writeVisitsResponse.errors.length > 0) {
                    visits.forEach((visit, index) => {
                        const visitToSave = {...visit};
                        visitToSave.errors = writeVisitsResponse.errors;
                        visitToSave.syncState = 'error';
                        visitsToSave.push(visitToSave);
                    });
                } else if (writeVisitsResponse.resources && writeVisitsResponse.resources.length > 0) {
                    writeVisitsResponse.resources.forEach((visitFromServer, index) => {
                        const originalVisit = visits[index];
                        const visitToSave = {...originalVisit};

                        if (visitFromServer.statusSync) {
                            visitToSave.statusSync = visitFromServer.statusSync;
                            
                            if (shouldRetry(visitFromServer.statusSync)) {
                                visitToSave.syncState = SYNC_STATE_DIRTY;
                                hasDeadlock = true;
                            } else {
                                visitToSave.syncState = SYNC_STATE_CLEAN;
                            }
                        } else {
                            if (visitFromServer.errors && visitFromServer.errors.length > 0){
                                visitToSave.errors = visitFromServer.errors;
                                visitToSave.syncState = SYNC_STATE_ERROR;
                            } else {
                                visitToSave.syncState = SYNC_STATE_CLEAN;
                            }
                        }

                        visitsToSave.push(visitToSave);
                    });
                }

                emitDeadlockIfDetected(hasDeadlock, ERROR_SOURCES.VISIT);

                if (visitsToSave.length > 0) {
                    console.log('visitService: writeVisitsToServer - About to save visitsToSave to DB');
                    return this.save(visitsToSave, {store: config.store, doNotClear: true})
                        .then(() => {
                            console.log('visitService: writeVisitsToServer - DB save complete, returning visitsToSave');
                            return visitsToSave;
                        });
                } else {
                    console.log('visitService: writeVisitsToServer - No visits to save, returning empty array');
                    return Promise.resolve(visitsToSave);
                }
            })
            .catch(async error => {
                const connectionLost = api.isConnectionLost(error);

                if (connectionLost){
                    const errorVisits = visits.map(v => {
                        const errorVisit = {
                            ...v,
                            statusSync: SYNC_STATUS.ERROR,
                            syncState: SYNC_STATE_DIRTY,
                            ...(v.visitStatus === 'start' && { isPendSendStartSessInfo: true })
                        };
                        return errorVisit;
                    });
                    await this.save(errorVisits, { store: config.store, doNotClear: true });
                    return errorVisits;
                } else {
                    console.log("Error thrown while writing visits to server: ");
                    console.log(error);
                    return Promise.reject(error);
                }
            });
    }

    dependentWriteToServer = async (config, savedAppointments = []) => {
        const visits = await this.repository.loadAllByProperty('syncState', SYNC_STATE_DIRTY);

        if (!visits || visits.length === 0) {
            return Promise.resolve([]);
        }

        const visitsToProcess = [];
        const visitsToCleanup = [];
        const savedAppointmentsBySyncId = this.createAppointmentMapForVisits(savedAppointments);

        for (const visit of visits) {
            try {
                const result = await this.processVisitForWrite(
                    visit,
                    savedAppointmentsBySyncId
                );

                if (result.shouldCleanup) {
                    visitsToCleanup.push(visit);
                } else if (result.shouldProcess) {
                    visitsToProcess.push(visit);
                }
            } catch (error) {
                console.error(`[dependentWriteToServer] Error loading appointment for visit ${visit.evvVisitId}:`, error);
            }
        }

        await this.cleanupOrphanedVisits(visitsToCleanup, config);

        return this.finalizeVisitProcessing(visitsToProcess, config);
    }

    createAppointmentMapForVisits = (savedAppointments) => {
        return new Map(
            (savedAppointments || []).filter(a => a.syncId).map(a => [a.syncId, a])
        );
    }

    processVisitForWrite = async (visit, savedAppointmentsBySyncId) => {
        // Visits without appointmentId are always processed
        if (!visit.appointmentId) {
            return { shouldProcess: true, shouldCleanup: false };
        }

        const appointment = await this.findLinkedAppointmentForVisit(
            visit,
            savedAppointmentsBySyncId
        );

        if (!appointment) {
            return this.handleMissingAppointment(visit);
        }

        const canProcessVisit = this.canProcessVisit(appointment);

        if (!canProcessVisit) {
            return { shouldProcess: false, shouldCleanup: false };
        }

        await this.updateVisitIdsIfNeeded(visit, appointment, savedAppointmentsBySyncId);

        return { shouldProcess: true, shouldCleanup: false };
    }

    findLinkedAppointmentForVisit = async (visit, savedAppointmentsBySyncId) => {
        // Priority 1: Use fresh data from write response if available
        const appointmentFromResponse = this.getAppointmentFromResponse(
            visit,
            savedAppointmentsBySyncId
        );

        if (appointmentFromResponse) {
            return appointmentFromResponse;
        }

        // Priority 2: Search in IndexedDB by appointmentId
        const appointmentByKey = await this.getAppointmentByKey(visit.appointmentId);

        if (appointmentByKey) {
            return appointmentByKey;
        }

        // Priority 3: Search in IndexedDB by syncId
        return await this.getAppointmentBySyncId(visit.syncId);
    }

    getAppointmentFromResponse = (visit, savedAppointmentsBySyncId) => {
        if (!visit.syncId) {
            return null;
        }
        return savedAppointmentsBySyncId.get(visit.syncId) || null;
    }

    getAppointmentByKey = async (appointmentId) => {
        return await evvRepository.evvDb.appointment.get(appointmentId);
    }

    getAppointmentBySyncId = async (syncId) => {
        if (!syncId) {
            return null;
        }

        return await evvRepository.evvDb.appointment
            .filter(a => a.syncId === syncId)
            .first();
    }

    handleMissingAppointment = (visit) => {
        // Orphaned visits with negative appointmentId should be cleaned up
        if (visit.appointmentId < 0) {
            return { shouldProcess: false, shouldCleanup: true };
        }
        return { shouldProcess: false, shouldCleanup: false };
    }

    canProcessVisit = (appointment) => {
        return canProcess(appointment.statusSync) || !appointment.statusSync;
    }

    updateVisitIdsIfNeeded = async (visit, appointment, savedAppointmentsBySyncId) => {
        const appointmentFromResponse = visit.syncId ? savedAppointmentsBySyncId.get(visit.syncId) : null;

        if (!this.shouldUpdateVisitIds(appointmentFromResponse, visit)) {
            return;
        }

        const oldEvvVisitId = visit.evvVisitId;
        
        visit.evvVisitId = appointmentFromResponse.evvVisitId;
        visit.appointmentId = appointmentFromResponse.appointmentId;
        visit.appointment = appointmentFromResponse;

        await evvRepository.evvDb.visit.put(visit);
        
        // Delete old negative-key record to prevent infinite sync loop
        // Dexie put() creates a NEW record at the positive key without removing the old one
        await evvRepository.evvDb.visit.delete(oldEvvVisitId);
    }

    shouldUpdateVisitIds = (appointmentFromResponse, visit) => {
        return appointmentFromResponse
            && appointmentFromResponse.evvVisitId > 0
            && visit.evvVisitId < 0;
    }

    cleanupOrphanedVisits = async (visitsToCleanup, config) => {
        if (visitsToCleanup.length === 0) {
            return;
        }

        for (const orphanedVisit of visitsToCleanup) {
            try {
                await this.deleteByPrimaryKey(orphanedVisit.evvVisitId);
                await this.removeVisitFromRedux(orphanedVisit, config);
            } catch (deleteError) {
                console.error(`Error deleting orphaned visit ${orphanedVisit.evvVisitId}:`, deleteError);
            }
        }
    }

    removeVisitFromRedux = async (visit, config) => {
        try {
            const dispatch = getDispatchFromConfig(config);
            if (dispatch) {
                dispatch(this.operationRemove(visit));
            }
        } catch (reduxError) {
            console.warn('Error removing orphaned visit from Redux:', reduxError);
        }
    }

    finalizeVisitProcessing = (visitsToProcess, config) => {
        if (visitsToProcess.length === 0) {
            return Promise.resolve([]);
        }

        return this.writeVisitsToServer(visitsToProcess, config);
    }

    startClientVisit = (store, appointment, user, startTimer, handleVisitStarted) => {
        return (position) => {

            const visit = {
                appointmentId: appointment.appointmentId,
                clientId: appointment.clientId,
                evvVisitId: appointment.evvVisitId,
                organizationId: appointment.organizationId,
                staffId: appointment.staffId,
                syncId: appointment.syncId
            };

            try {
                if (!position?.coords?.latitude || !position?.coords?.longitude) {
                    syncService.deviceInfoService.trackDeviceInfo({
                        eventType: EVENT_TYPES.NULL_POSITION,
                        actionType: ACTION_TYPES.START,
                        source: SOURCES.VISIT_START_INVALID_POS,
                        user,
                        visit
                    });
                }
            } catch (guardError) {
                syncService.deviceInfoService.trackDeviceInfo({
                    eventType: EVENT_TYPES.EXCEPTION,
                    actionType: ACTION_TYPES.START,
                    source: SOURCES.VISIT_START_EXCEPTION,
                    user,
                    visit,
                    error: guardError
                });
            }

            this.loadVisitByAppointmentId(appointment.appointmentId, {store})
                .then((activeVisit) => {
                    const actualStartDateInstance = nowAsDateTime(true);//check
                    const diffInMinutes = appointment.endDateInstance.diff(appointment.startDateInstance, ['minutes']);
                    const actualEndDateInstance = actualStartDateInstance.plus(diffInMinutes);
                    startTimer(actualStartDateInstance);

                    const initialVisit = activeVisit ? activeVisit : this.createInitialVisit(appointment);
                    const scheduledEndOriginal = appointment.scheduledEndDateInstance
                        ? appointment.scheduledEndDateInstance.toMillis()
                        : (appointment.scheduledStartDateInstance
                            ? appointment.scheduledStartDateInstance.plus(diffInMinutes).toMillis()
                            : null);

                    const visit = {
                        ...initialVisit,
                        startLatitude: position.coords.latitude,
                        startLongitude: position.coords.longitude,
                        startDateTime: actualStartDateInstance.toMillis(),
                        endOriginalDateTime: activeVisit?.endOriginalDateTime || scheduledEndOriginal || initialVisit.endDateTime,
                        actualStartDateInstance,
                        actualEndDateInstance,
                        beginActualDateTime: dateToStringWithFormat(actualStartDateInstance, APPOINTMENT_START_STOP_DATE_TIME_FORMAT),
                        endActualDateTime: dateToStringWithFormat(actualEndDateInstance, APPOINTMENT_START_STOP_DATE_TIME_FORMAT),
                        visitStatus: 'start',
                        complete: VISIT_STARTED
                    }

                    this.trackVisitDeviceInfo({
                        eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                        actionType: ACTION_TYPES.START,
                        source: SOURCES.VISIT_START_SNAPSHOT,
                        user,
                        visit
                    });

                    this.saveVisit(visit, {store, doNotClear: true})
                        .then(savedVisit => {
                            if (!savedVisit) {
                                return;
                            }

                            const appointmentToSave = {...appointment};
                            appointmentToSave.status = 'In Session';
                            appointmentToSave.appointmentDate = actualStartDateInstance.toISO({ suppressSeconds: true, suppressMilliseconds: true, includeOffset: false });

                            // Preserve the original scheduled times before modifying (both instances and strings)
                            if (!appointmentToSave.scheduledStartDateInstance) {
                                appointmentToSave.scheduledStartDateInstance = appointment.startDateInstance;
                                appointmentToSave.scheduledStartTime = appointment.startTime;
                            }
                            if (!appointmentToSave.scheduledEndDateInstance) {
                                appointmentToSave.scheduledEndDateInstance = appointment.endDateInstance;
                                appointmentToSave.scheduledEndTime = appointment.endTime;
                            }

                            // Update to actual session times
                            appointmentToSave.startDateInstance = actualStartDateInstance;
                            appointmentToSave.startTime = dateToStringWithFormat(actualStartDateInstance, DEFAULT_TIME_FORMAT);
                            appointmentToSave.endDateInstance = actualEndDateInstance;
                            appointmentToSave.endTime = dateToStringWithFormat(actualEndDateInstance, DEFAULT_TIME_FORMAT);

                            activityLogRepository.saveActivityLog(user, 'Session Started', savedVisit);

                            if (handleVisitStarted) {
                                handleVisitStarted(savedVisit, appointment);
                            }
                        })
                        .catch(err => {
                            console.error('[startClientVisit] ERROR in saveVisit:', err);
                            syncService.deviceInfoService.trackDeviceInfo({
                                eventType: EVENT_TYPES.FIELD_NULL,
                                actionType: ACTION_TYPES.START,
                                source: SOURCES.VISIT_START_SAVE_ERROR,
                                user,
                                visit,
                                error: err
                            });
                        });
                })
                .catch(err => {
                    console.error('[startClientVisit] ERROR in loadVisitByAppointmentId:', err);

                    syncService.deviceInfoService.trackDeviceInfo({
                        eventType: EVENT_TYPES.FIELD_NULL,
                        actionType: ACTION_TYPES.START,
                        source: SOURCES.VISIT_START_LOAD_ERROR,
                        user,
                        visit,
                        error: err
                    });
                });
        }
    }

    endClientVisit = (store, user, client, endTimer, handleVisitEnded, visit) => {
        return (position) => {
            try {
                if (!position?.coords?.latitude || !position?.coords?.longitude) {
                    syncService.deviceInfoService.trackDeviceInfo({
                        eventType: EVENT_TYPES.FIELD_NULL,
                        actionType: ACTION_TYPES.STOP,
                        source: SOURCES.VISIT_END_INVALID_POS,
                        user,
                        visit
                    });
                }
            } catch (guardError) {
                syncService.deviceInfoService.trackDeviceInfo({
                    eventType: EVENT_TYPES.FIELD_NULL,
                    actionType: ACTION_TYPES.STOP,
                    source: SOURCES.VISIT_END_POS_GUARD_ERROR,
                    user,
                    visit,
                    error: guardError
                });
            }

            this.loadActiveClientVisit(client, {store})
                .then((activeVisit) => {
                    const actualEndDateInstance = nowAsDateTime(true);

                    if(endTimer) {
                        endTimer(actualEndDateInstance);
                    }
                    if(activeVisit === undefined && visit !== null) {
                        activeVisit = visit;
                    }
                    const visitToSave = {
                        ...activeVisit,
                        endLatitude: position.coords.latitude,
                        endLongitude: position.coords.longitude,
                        endDateTime: actualEndDateInstance.toMillis(),
                        endActualDateTime: dateToStringWithFormat(actualEndDateInstance, APPOINTMENT_START_STOP_DATE_TIME_FORMAT),
                        actualEndDateInstance,
                        visitStatus: 'end',
                    };

                    this.trackVisitDeviceInfo({
                        eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                        actionType: ACTION_TYPES.STOP,
                        source: SOURCES.VISIT_END_SNAPSHOT,
                        user,
                        visit: visitToSave
                    });

                    this.saveVisit(visitToSave, {store, doNotClear: true})
                        .then((savedVisit) => {
                            if (savedVisit) {
                                store.dispatch(this.operationReplace(savedVisit));

                                activityLogRepository.saveActivityLog(user, 'Session Ended', savedVisit);

                                if (handleVisitEnded) {
                                    handleVisitEnded(savedVisit);
                                }
                            }
                        });

                })
                .catch((ex) => {
                    console.log("Unable to save ended visit:");
                    console.log(ex);
                    syncService.deviceInfoService.trackDeviceInfo({
                        eventType: EVENT_TYPES.FIELD_NULL,
                        actionType: ACTION_TYPES.STOP,
                        source: SOURCES.VISIT_END_LOAD_ERROR,
                        user,
                        visit,
                        error: ex
                    });
                });
        }
    }

    cleanupVisit = (visit) => {
        const cleanVisit = {...visit};

        delete cleanVisit.endLatitude;
        delete cleanVisit.endLongitude;
        delete cleanVisit.endDateTime;
        delete cleanVisit.endAdjustedDateTime;
        delete cleanVisit.actualEndDateInstance;
        delete cleanVisit.verificationDeclinedReason;
        delete cleanVisit.verificationDeclinedReason;
        delete cleanVisit.verificationDeclinedComments;
        delete cleanVisit.signatureMissingReason;
        delete cleanVisit.signatureMissingComments;
        delete cleanVisit.addressDistanceReason;
        delete cleanVisit.addressDistanceComments;
        delete cleanVisit.editTimesReason;
        delete cleanVisit.editTimesComments;
        delete cleanVisit.editTimesEventType;
        delete cleanVisit.signature;
        delete cleanVisit.hasSignature;
        delete cleanVisit.timesVerified;
        delete cleanVisit.servicesVerified;

        return cleanVisit;
    }

    cancelVerifyClientVisit = (store, user, client) => {
        this.loadActiveClientVisit(client, {store})
            .then((activeVisit) => {
                if(activeVisit) {
                    this.trackVisitDeviceInfo({
                        eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                        actionType: ACTION_TYPES.CANCEL_VERIFY,
                        source: SOURCES.CANCEL_VERIFY_SNAPSHOT,
                        user,
                        visit: activeVisit
                    });

                    const visitToSave = {
                        ...activeVisit,
                        visitStatus: 'start'
                    };
                    delete visitToSave.endLatitude;
                    delete visitToSave.endLongitude;
                    delete visitToSave.endAdjustedDateTime;
                    delete visitToSave.endActualDateTime;
                    delete visitToSave.actualEndDateInstance;

                    this.save([visitToSave], {store, doNotClear: true})
                        .then(() => {
                            store.dispatch(this.operationReplace(visitToSave));

                            activityLogRepository.saveActivityLog(user, 'Session Verify Cancelled', visitToSave);
                        });
                }
            })
            .catch((ex) => {
                console.log("Unable to cancel visit verification:");
                console.log(ex);
            });
    }

    verifyClientVisit = (store, user, client, verifiedVisit) => {
        console.log("verifyClientVisit");
        return this.loadActiveClientVisit(client, {store})
            .then((activeVisit) => {
                console.log("activeVisit:");
                console.log(activeVisit);
                console.log("verifiedVisit:");
                console.log(verifiedVisit);
                const startDateTimeFromEditTime = dateToStringWithFormat(DateTime.fromMillis(verifiedVisit.startDateTime), DEFAULT_DATE_TIME_FORMAT);
                const endDateTimeFromEditTime = dateToStringWithFormat(DateTime.fromMillis(verifiedVisit.endDateTime), DEFAULT_DATE_TIME_FORMAT);
                const startDateTimeFromVisitVerification = dateToStringWithFormat(DateTime.fromMillis(activeVisit.startDateTime), DEFAULT_DATE_TIME_FORMAT);
                const endDateTimeFromVisitVerification = dateToStringWithFormat(DateTime.fromMillis(activeVisit.endDateTime), DEFAULT_DATE_TIME_FORMAT);
                const visit = {
                    ...this.cleanupVisit(activeVisit),
                    ...verifiedVisit,
                    isUpdatedBeginAdjustedDateTime : false,
                    isUpdatedEndAdjustedDateTime : false,
                    endActualDateTime: activeVisit.endActualDateTime,
                    beginActualDateTime: activeVisit.beginActualDateTime,
                    visitStatus: 'verify',
                    complete: VISIT_COMPLETE
                };

                this.trackVisitDeviceInfo({
                    eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                    actionType: ACTION_TYPES.VERIFY,
                    source: SOURCES.VERIFY_SNAPSHOT_MERGE,
                    user,
                    visit
                });
                if(startDateTimeFromEditTime !== startDateTimeFromVisitVerification) {
                    visit.beginAdjustedDateTime = dateToStringWithFormat(DateTime.fromMillis(verifiedVisit.startDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
                    visit.isUpdatedBeginAdjustedDateTime = true;
                }
                if(endDateTimeFromEditTime !== endDateTimeFromVisitVerification) {
                    visit.endAdjustedDateTime = dateToStringWithFormat(DateTime.fromMillis(verifiedVisit.endDateTime), APPOINTMENT_START_STOP_DATE_TIME_FORMAT);
                    visit.isUpdatedEndAdjustedDateTime = true;
                }

                console.log("visitToSaveLocal:");
                console.log(visit);

                const appointmentToSave = {...visit.appointment};
                appointmentToSave.status = verifiedVisit.appointmentStatus;
                appointmentToSave.startDateInstance = DateTime.fromMillis(verifiedVisit.startDateTime);
                appointmentToSave.endDateInstance = DateTime.fromMillis(verifiedVisit.endDateTime);
                appointmentToSave.appointmentDate = appointmentToSave.startDateInstance.toISO({ suppressSeconds: true, suppressMilliseconds: true, includeOffset: false });
                appointmentToSave.startTime = defaultTimeAsString(appointmentToSave.startDateInstance);
                appointmentToSave.endTime = defaultTimeAsString(appointmentToSave.endDateInstance);
                appointmentToSave.actualStartTime = appointmentToSave.startTime;
                appointmentToSave.actualEndTime = appointmentToSave.endTime;
                if (verifiedVisit.appointmentStatus === 'Kept' && verifiedVisit.activityDetailDescriptorList) {
                    appointmentToSave.activityDetailDescriptorList = verifiedVisit.activityDetailDescriptorList;
                }

                console.log("appointmentSave");
                console.log(appointmentToSave);

                return syncService.appointmentService.saveAppointment(appointmentToSave, {store, doNotClear: true, ignoreVisitSave: true})
                    .then(appAfterSave => {
                    if (!appAfterSave || !canProcess(appAfterSave.statusSync)) {
                        visit.statusSync = appAfterSave?.statusSync || SYNC_STATUS.ERROR;
                        visit.syncState = SYNC_STATE_DIRTY;
                        visit.appointment = appAfterSave;
                        this.save([visit], {store, doNotClear: true})
                            .then(() => {
                                store.dispatch(this.operationReplace(visit));
                            });
                        return;
                    }

                    // Verify exits previous information. Send the previous information from internal DB to write/visit
                    return this.loadOfflineVisit().then((visitArray) => {
                        if(visitArray && visitArray.length > 0){
                            visitArray.forEach((visitDB) => {
                                if (visitDB.appointmentId === appAfterSave.appointmentId 
                                    && visitDB.syncState === SYNC_STATE_DIRTY 
                                    && visitDB.isPendSendStartSessInfo === true) {
                                        console.log('A8')
                                    visit.evvVisitId = appAfterSave.evvVisitId;
                                    visit.appointmentId = appAfterSave.appointmentId;
                                    visit.appointment = appAfterSave;
                                    visit.isPendSendStartSessInfo = true;
                                    console.log('A9')
                                    visit.beginActualDateTime = visitDB.beginActualDateTime;
                                    visit.startLatitude = visitDB.startLatitude;
                                    visit.startLongitude = visitDB.startLongitude;
                                    visit.beginDateTime = visitDB.beginDateTime;
                                    visit.endOriginalDateTime = visitDB.endOriginalDateTime || visitDB.endDateTime;
                                }
                            });
                            visit.statusSync = appAfterSave.statusSync;
                            this.trackVisitDeviceInfo({
                                eventType: EVENT_TYPES.FIELD_SNAPSHOT,
                                actionType: ACTION_TYPES.VERIFY,
                                source: SOURCES.VERIFY_SNAPSHOT_SAVE,
                                user,
                                visit
                            });
                            console.log('visitService: verifyClientVisit - About to call saveVisit');
                            return this.saveVisit(visit, {store, doNotClear: true})
                            .then(savedVisit => {
                                console.log('visitService: verifyClientVisit - saveVisit completed, dispatching and logging');
                                store.dispatch(this.operationRemove(visit));
                                activityLogRepository.saveActivityLog(user, 'Session Ended', savedVisit);
                                console.log('visitService: verifyClientVisit - Final .then() finishing, returning savedVisit');
                                return savedVisit;
                            })
                            .catch(err => {
                                console.log("Error attempting saveVisit() call");
                                console.log(err);
                                this.cancelVerifyClientVisit(store, user, client);
                                return Promise.reject(err);
                            });
                        }
                    }).catch((error) => {
                        console.log('Error: ', error);
                        return Promise.reject(error);
                    });
                });
            })
            .catch((ex) => {
                console.log("Unable to save verified visit:");
                console.log(ex);
                this.cancelVerifyClientVisit(store, user, client);
            });
    }

    isInformationOffline = (config) => {
        const state = config.store.getState();
        return api.isOfflineAtLogin() || appCache.isOffline(state);
    }

    loadActiveClientVisit = (client, config) => {
        return evvRepository.evvDb.visit.where({clientId: client.clientId, complete: VISIT_STARTED}).first()
            .then((activeVisit) => this.afterLoad([activeVisit], config)[0]);
    }

    loadOfflineVisit = () =>{
        return evvRepository.evvDb.visit.toArray();
    }

    loadVisitByAppointmentId = (appointmentId, config) => {
        return evvRepository.evvDb.visit.where({appointmentId: appointmentId}).first()
            .then((visit) => {
                return visit ? this.afterLoad([visit], config)[0] : null;
            });
    }

    isSyncRequired = () => {
        return this.repository.loadAllByProperty('syncState', 'dirty')
            .then(visits => {
                return Promise.resolve(visits.length > 0);
            });
    }

    updateVisit = async (originalAppointment, appointmentToSave) => {
        const updateData = {
            "evvVisitId": appointmentToSave.evvVisitId,
            "appointmentId": appointmentToSave.appointmentId,
            "appointment": appointmentToSave
        };
        
        if (appointmentToSave.syncId) {
            updateData.syncId = appointmentToSave.syncId;
        }
        if (appointmentToSave.statusSync) {
            updateData.statusSync = appointmentToSave.statusSync;
        }
        
        evvRepository.evvDb.visit
            .where("evvVisitId").equals(originalAppointment.appointmentId)
            .modify(updateData);
    }

    getVisit = async (originalAppointmentId) => {
        return evvRepository.evvDb.visit
            .where("evvVisitId").equals(originalAppointmentId)
            .toArray();
    }

    getInSessionVisit = async() => {
        return evvRepository.evvDb.visit
            .where({visitStatus: 'start'})
            .toArray();
    }
}

export default VisitService;
